import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Building2, Mail, Phone, Globe, Tag } from 'lucide-react';
import api from '../../../api';

export default function CreatePartnerPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'Dance',
        artforms: '',
        services: '',
        contact_person: '',
        role: '',
        phone: '',
        email: '',
        website: '',
        logo_url: ''
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEdit);
    const [categories, setCategories] = useState(['Dance', 'Percussion', 'Cultural Association']);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isEdit) {
            loadPartner();
        }
        loadCategories();
    }, [id]);

    const loadCategories = async () => {
        try {
            const cats = await api.getPartnerCategories();
            if (cats.length > 0) {
                setCategories(cats);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadPartner = async () => {
        try {
            const partner = await api.getPartner(id);
            setFormData({
                name: partner.name || '',
                description: partner.description || '',
                category: partner.category || 'Dance',
                artforms: partner.artforms?.join(', ') || '',
                services: partner.services?.join(', ') || '',
                contact_person: partner.contact_person || '',
                role: partner.role || '',
                phone: partner.phone || '',
                email: partner.email || '',
                website: partner.website || '',
                logo_url: partner.logo_url || ''
            });
        } catch (error) {
            console.error('Error loading partner:', error);
            alert('Error loading partner: ' + error.message);
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const partnerData = {
                ...formData,
                artforms: formData.artforms.split(',').map(t => t.trim()).filter(Boolean),
                services: formData.services.split(',').map(t => t.trim()).filter(Boolean)
            };

            if (isEdit) {
                await api.updatePartner(id, partnerData);
            } else {
                await api.createPartner(partnerData);
            }

            navigate('/admin');
        } catch (error) {
            alert('Error saving partner: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <main className="main">
                <div className="container" style={{ paddingTop: '40px' }}>
                    <div className="skeleton" style={{ height: '600px' }} />
                </div>
            </main>
        );
    }

    return (
        <main className="main">
            <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px', maxWidth: '900px' }}>
                {/* Header */}
                <div style={{ marginBottom: '32px' }}>
                    <button
                        className="btn btn-ghost"
                        onClick={() => navigate('/admin')}
                        style={{ marginBottom: '16px' }}
                    >
                        <ArrowLeft size={18} />
                        Back to Admin
                    </button>
                    <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
                        {isEdit ? 'Edit Partner' : 'Add New Partner'}
                    </h1>
                    <p style={{ color: 'var(--text-muted)' }}>
                        {isEdit ? 'Update partner organization details' : 'Add a new partner organization to the directory'}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className="card" style={{ padding: '32px' }}>
                        {/* Organization Information */}
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                            <Building2 size={20} style={{ marginRight: '8px' }} />
                            Organization Information
                        </h2>

                        <div className="form-group">
                            <label className="form-label">Organization Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Swathi Dancers"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-input"
                                rows={5}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Describe the organization, its mission, and activities..."
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Category *</label>
                            <select
                                className="form-input"
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                required
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Logo URL</label>
                            <input
                                type="url"
                                className="form-input"
                                value={formData.logo_url}
                                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                                placeholder="https://example.com/logo.png"
                            />
                            {formData.logo_url && (
                                <div style={{ marginTop: '12px' }}>
                                    <img
                                        src={formData.logo_url}
                                        alt="Logo Preview"
                                        style={{
                                            maxWidth: '120px',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            border: '1px solid var(--border)'
                                        }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}
                        </div>

                        <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

                        {/* Arts & Services */}
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                            <Tag size={20} style={{ marginRight: '8px' }} />
                            Arts & Services
                        </h2>

                        <div className="form-group">
                            <label className="form-label">Art Forms (comma-separated)</label>
                            <textarea
                                className="form-input"
                                rows={3}
                                value={formData.artforms}
                                onChange={(e) => setFormData({ ...formData, artforms: e.target.value })}
                                placeholder="e.g., Bharatanatyam, Carnatic Music, Folk Dance"
                            />
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px' }}>
                                List all art forms practiced, separated by commas
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Services Offered (comma-separated)</label>
                            <textarea
                                className="form-input"
                                rows={3}
                                value={formData.services}
                                onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                                placeholder="e.g., Performance, Classes for schools, Equipment rental"
                            />
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px' }}>
                                List all services provided, separated by commas
                            </div>
                        </div>

                        <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid var(--border)' }} />

                        {/* Contact Information */}
                        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                            Contact Information
                        </h2>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="form-group">
                                <label className="form-label">Contact Person</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.contact_person}
                                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                                    placeholder="e.g., John Doe"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Role / Position</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    placeholder="e.g., Artistic Director"
                                />
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className="form-group">
                                <label className="form-label">
                                    <Phone size={16} style={{ marginRight: '6px' }} />
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    className="form-input"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="e.g., 91234567"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <Mail size={16} style={{ marginRight: '6px' }} />
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="e.g., contact@organization.sg"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Globe size={16} style={{ marginRight: '6px' }} />
                                Website
                            </label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.website}
                                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                placeholder="e.g., www.organization.sg (without https://)"
                            />
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px' }}>
                                Enter without http:// or https://
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate('/admin')}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                            style={{ minWidth: '150px' }}
                        >
                            {loading ? (
                                'Saving...'
                            ) : (
                                <>
                                    <Save size={18} />
                                    {isEdit ? 'Update Partner' : 'Create Partner'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
}

